import {
  TEASER_HIDE_AFTER_MAX,
  TEASER_HIDE_AFTER_MIN,
  TEASER_MAX_QUICK_REPLIES,
  TEASER_SHOW_AFTER_MAX,
  TEASER_SHOW_AFTER_MIN,
  TeaserBlock,
  TeaserConfig,
  TeaserPage,
  TeaserSection,
} from '@/shared/types/teaser';

import { normalizeWebsiteUrl, titleFromPath } from './page-url';
import {
  FieldValue,
  TeaserForm,
  TeaserPageForm,
  TeaserSectionForm,
  emptyTeaserForm,
  emptyTeaserPage,
  emptyTeaserSection,
} from './types';

/**
 * The boundary between the form's flat shape and the wire's discriminated
 * union.
 *
 * This module — not the JSX — is where "switching variant can never leave a
 * field the new variant forbids" is enforced. The orchestrator's schema rejects
 * `placeholder` on a `callout`, `message` on `question_chips`, `quick_replies`
 * on anything but chips, and anything at all on `none`, and a rejected save
 * surfaces as an error on a field the author cannot even see. So the form keeps
 * every variant's fields resident and this drops the ones the selected variant
 * does not own.
 */

type VariantFields = {
  variant: TeaserForm['variant'];
  message: string;
  placeholder: string;
  quick_replies: FieldValue[];
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

/**
 * Parses one of the timing fields.
 *
 * Empty, zero, negative and unparseable all mean "off", which on the wire is
 * *absent* rather than a number. Only a positive value is clamped — coercing a
 * cleared field up to the minimum would turn "no delay" into "wait a second",
 * which is a behaviour change the author did not ask for.
 */
export const toSeconds = (raw: string, min: number, max: number): number | undefined => {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return undefined;
  }
  return clamp(Math.round(parsed), min, max);
};

/** Trims, drops blanks, then caps. Trimming first so `'  '` is not a reply. */
const toStrings = (items: FieldValue[] | undefined, cap: number): string[] =>
  (items ?? [])
    .map((item) => (item?.value ?? '').trim())
    .filter(Boolean)
    .slice(0, cap);

/**
 * Narrows the resident fields down to the chosen variant's own.
 *
 * Returns `null` for chips with nothing to offer. That is an incomplete
 * authoring state rather than an instruction — the form validates against it
 * first, and this is the backstop so we never post
 * `{ variant: 'question_chips' }` without the `quick_replies` the schema
 * requires. `{ variant: 'none' }`, by contrast, is a real instruction and
 * always survives.
 */
export const toTeaserConfig = (fields: VariantFields): TeaserConfig | null => {
  switch (fields.variant) {
    case 'none':
      return { variant: 'none' };

    case 'question_chips': {
      const quickReplies = toStrings(fields.quick_replies, TEASER_MAX_QUICK_REPLIES);
      return quickReplies.length
        ? { variant: 'question_chips', quick_replies: quickReplies }
        : null;
    }

    case 'composer': {
      const message = (fields.message ?? '').trim();
      const placeholder = (fields.placeholder ?? '').trim();
      return {
        variant: 'composer',
        ...(message && { message }),
        ...(placeholder && { placeholder }),
      };
    }

    case 'callout':
    default: {
      const message = (fields.message ?? '').trim();
      // `message` stays optional: `{ variant: 'callout' }` with no copy is valid
      // on the wire, and the widget still falls back to `welcome_message`.
      return { variant: 'callout', ...(message && { message }) };
    }
  }
};

/**
 * A section needs a name and at least one selector to mean anything. Anything
 * less is a half-filled row, and dropping it is kinder than posting a section
 * the widget can never resolve.
 */
export const toTeaserSection = (form: TeaserSectionForm): TeaserSection | null => {
  const id = (form.id ?? '').trim();
  const match = toStrings(form.match, Number.MAX_SAFE_INTEGER);
  if (!id || !match.length) {
    return null;
  }
  const config = toTeaserConfig(form);
  if (!config) {
    return null;
  }
  return { ...config, id, match } as TeaserSection;
};

/** A page hint is the whole point of the entry, so an empty one is dropped. */
export const toTeaserPage = (form: TeaserPageForm): TeaserPage | null => {
  const url = (form.url ?? '').trim();
  if (!url) {
    return null;
  }
  /*
    A page has no teaser of its own to author any more — it is a container for
    sections — so the only page-level config left is whatever a legacy entry
    was saved with, which `fromTeaserPage` parked in these fields and nothing
    on screen can change.

    `{ variant: 'none' }` is the right default rather than a bare callout: per
    the type, a page match does not fall back to the block, so a callout with
    no copy here would *replace* the block teaser with nothing on every page it
    matched. `none` says the same thing the design does — this page is its
    sections.
  */
  const config = toTeaserConfig(form) ?? { variant: 'none' };
  const guidelines = (form.guidelines ?? '').trim();
  const sections = (form.sections ?? [])
    .map(toTeaserSection)
    .filter((section): section is TeaserSection => section !== null);
  return {
    ...config,
    url,
    ...(guidelines && { guidelines }),
    ...(sections.length && { sections }),
  } as TeaserPage;
};

/**
 * Whether the form still holds exactly its defaults.
 *
 * `{ variant: 'callout' }` with no copy is a valid configuration, so the
 * serialiser cannot treat a bare callout as "nothing". And since `callout` is
 * also the form default, this is what tells "the author chose a bare callout"
 * from "never touched": without it, saving the accordion for an unrelated
 * reason would write a `teaser` key onto every integration that never had one.
 */
export const isTeaserFormPristine = (form: TeaserForm): boolean => {
  const pristine = emptyTeaserForm();
  return (
    form.variant === pristine.variant &&
    !(form.message ?? '').trim() &&
    !(form.placeholder ?? '').trim() &&
    !toStrings(form.quick_replies, TEASER_MAX_QUICK_REPLIES).length &&
    !(form.show_after ?? '').trim() &&
    !(form.hide_after ?? '').trim() &&
    // `website_url` and `per_page` are deliberately not counted.
    //
    // `website_url` arrives prefilled from the integration's trusted domains,
    // and `per_page` is derived from whether there were pages to begin with.
    // Counting either would make an untouched form look authored and write a
    // `teaser` key onto every integration that has never had one — the exact
    // failure this function exists to prevent. Neither configures anything by
    // itself, and both only reach the wire alongside pages.
    !(form.pages ?? []).length
  );
};

/**
 * The whole block, or `null` when nothing in the form survives narrowing —
 * which must clear the key rather than post an empty object.
 *
 * A pristine form serialises to a bare `callout`, not to `null`, because that
 * is a valid configuration. Use `isTeaserFormPristine` to decide whether to
 * send the key at all.
 */
export const toTeaserBlock = (form: TeaserForm): TeaserBlock | null => {
  const config = toTeaserConfig(form);
  /*
    The switch, not the row count, decides whether pages reach the wire.

    Turning "Customize per webpage" off leaves the rows resident in the form so
    turning it back on returns them intact — so an emptied `pages` here is the
    author saying "not per page", and a populated one that the switch has
    hidden must not be sent.
  */
  const pages = form.per_page
    ? (form.pages ?? []).map(toTeaserPage).filter((page): page is TeaserPage => page !== null)
    : [];
  // Normalised on the way out, not as the author types: `https://moveo.ai/` and
  // `moveo.ai` must land on one stored value, or `website_url + url` produces a
  // double slash or a missing scheme.
  const websiteUrl = normalizeWebsiteUrl(form.website_url ?? '');
  const showAfter = toSeconds(form.show_after, TEASER_SHOW_AFTER_MIN, TEASER_SHOW_AFTER_MAX);
  const hideAfter = toSeconds(form.hide_after, TEASER_HIDE_AFTER_MIN, TEASER_HIDE_AFTER_MAX);

  const saysNothing =
    !config && !pages.length && showAfter === undefined && hideAfter === undefined;
  if (saysNothing) {
    return null;
  }

  return {
    // Chips with no replies at block level still leave the per-page teasers
    // worth sending, so fall back to the bare `callout` — which carries no copy
    // and therefore changes nothing on its own.
    ...(config ?? { variant: 'callout' }),
    ...(pages.length && { pages }),
    // Only sent when there are pages for it to serve. On its own it configures
    // nothing, and writing it to an integration with no page teasers would put
    // a field on the wire that nothing reads.
    ...(pages.length && websiteUrl && { website_url: websiteUrl }),
    ...(showAfter !== undefined && { show_after: showAfter }),
    ...(hideAfter !== undefined && { hide_after: hideAfter }),
  } as TeaserBlock;
};

/** `undefined` reads as "no field", which for a variant means the default. */
const variantOf = (config: TeaserConfig | null | undefined): TeaserForm['variant'] =>
  (config as { variant?: TeaserForm['variant'] })?.variant ?? 'callout';

const stringOf = (
  config: TeaserConfig | null | undefined,
  key: 'message' | 'placeholder'
): string => (config as Record<string, string | null | undefined>)?.[key] ?? '';

const repliesOf = (config: TeaserConfig | null | undefined): FieldValue[] =>
  ((config as { quick_replies?: string[] })?.quick_replies ?? []).map((value) => ({ value }));

/** Absent and `0` are the same thing to the widget, and both read as empty. */
const secondsOf = (value: number | null | undefined): string =>
  typeof value === 'number' && value > 0 ? String(value) : '';

const fromTeaserSection = (section: TeaserSection): TeaserSectionForm => ({
  ...emptyTeaserSection(),
  id: section.id,
  // A saved section always has at least one selector, but keep a row so the
  // disclosure is never an empty box.
  match: (section.match?.length ? section.match : ['']).map((value) => ({ value })),
  // Chips, whatever was saved: sections author quick actions only, so a stored
  // variant would leave the author editing a list that never reaches the wire.
  variant: 'question_chips',
  quick_replies: repliesOf(section),
});

const fromTeaserPage = (page: TeaserPage): TeaserPageForm => ({
  ...emptyTeaserPage(),
  url: page.url ?? '',
  /*
    Derived on load, not stored on the wire: `title` is authoring-only and the
    orchestrator has no field for it, so a saved page comes back nameless and
    the header would otherwise be blank. Deriving it here means it is computed
    once per load rather than on every render of the accordion header.
  */
  title: titleFromPath(page.url ?? ''),
  guidelines: page.guidelines ?? '',
  variant: variantOf(page),
  message: stringOf(page, 'message'),
  placeholder: stringOf(page, 'placeholder'),
  quick_replies: repliesOf(page),
  sections: (page.sections ?? []).map(fromTeaserSection),
});

/** Hydrates the form from saved config. Inverse of `toTeaserBlock`. */
export const fromTeaserBlock = (block: TeaserBlock | null | undefined): TeaserForm => {
  if (!block) {
    return emptyTeaserForm();
  }
  return {
    variant: variantOf(block),
    message: stringOf(block, 'message'),
    placeholder: stringOf(block, 'placeholder'),
    quick_replies: repliesOf(block),
    show_after: secondsOf(block.show_after),
    hide_after: secondsOf(block.hide_after),
    website_url: block.website_url ?? '',
    // The wire has no switch; having pages *is* the switch being on.
    per_page: !!block.pages?.length,
    pages: (block.pages ?? []).map(fromTeaserPage),
  };
};

/**
 * What to write to `config.teaser` when the accordion is saved: `undefined`
 * omits the key, `null` clears it, and a block replaces it.
 *
 * An untouched form that was never saved writes nothing. The default variant is
 * `callout`, so serialising it would add a `teaser` key to every integration
 * that has never had one, the first time anyone saved the accordion.
 */
export const teaserForSave = (
  form: TeaserForm,
  { hasSavedTeaser }: { hasSavedTeaser: boolean }
): TeaserBlock | null | undefined => {
  if (isTeaserFormPristine(form) && !hasSavedTeaser) {
    return undefined;
  }
  return toTeaserBlock(form);
};
